const RETAIL_SALE_DRAFTS_KEY = 'retail-sale:drafts:v1'
export const RETAIL_SALE_BROADCAST_CHANNEL = 'retail-sale:customer-display:v1'

/**
 * Create the local browser channel used by the POS and customer display.
 *
 * @returns {BroadcastChannel|null} Local channel when supported by the browser.
 */
export const createRetailSaleBroadcastChannel = () => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return null
  }

  return new window.BroadcastChannel(RETAIL_SALE_BROADCAST_CHANNEL)
}

const canUseStorage = () => {
  return typeof window !== 'undefined' && Boolean(window.localStorage)
}

const hasDraftContent = order => {
  return Boolean(
    (order.cartItems || []).length ||
      order.remarks ||
      order.search ||
      order.activeCategoryId !== null ||
      Number(order.invoice_discount_rate || 0) > 0 ||
      Number(order.invoice_discount_amount || 0) > 0
  )
}

/**
 * Restore retail-sale order drafts from browser storage.
 *
 * @returns {{orders: Array, activeOrderId: number|null}} Saved draft state.
 */
export const loadRetailSaleDrafts = () => {
  if (!canUseStorage()) return { orders: [], activeOrderId: null }

  try {
    const saved = JSON.parse(window.localStorage.getItem(RETAIL_SALE_DRAFTS_KEY) || 'null')
    if (!saved || !Array.isArray(saved.orders)) {
      return { orders: [], activeOrderId: null }
    }

    return {
      orders: saved.orders.filter(order => order && order.id),
      activeOrderId: saved.activeOrderId || null
    }
  } catch (error) {
    window.localStorage.removeItem(RETAIL_SALE_DRAFTS_KEY)
    return { orders: [], activeOrderId: null }
  }
}

/**
 * Save non-empty retail-sale order tabs to browser storage.
 *
 * @param {Array} orders Current order tabs.
 * @param {number} activeOrderId Active order tab identifier.
 * @returns {void}
 */
export const saveRetailSaleDrafts = (orders = [], activeOrderId = null) => {
  if (!canUseStorage()) return

  const drafts = orders.filter(hasDraftContent)
  if (!drafts.length) {
    window.localStorage.removeItem(RETAIL_SALE_DRAFTS_KEY)
    return
  }

  window.localStorage.setItem(
    RETAIL_SALE_DRAFTS_KEY,
    JSON.stringify({ orders, activeOrderId })
  )
}

/**
 * Remove all retail-sale drafts from browser storage.
 *
 * @returns {void}
 */
export const clearRetailSaleDrafts = () => {
  if (canUseStorage()) window.localStorage.removeItem(RETAIL_SALE_DRAFTS_KEY)
}

/**
 * Create a client idempotency key for a retail-sale payment attempt.
 *
 * @returns {string} UUID-shaped idempotency key.
 */
export const createPaymentIdempotencyKey = () => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.random() * 16 | 0
    const value = character === 'x' ? random : random & 0x3 | 0x8
    return value.toString(16)
  })
}

/**
 * Check whether a product grid scroll has reached its next-page threshold.
 *
 * @param {Object} order Current order product paging state.
 * @param {Object} grid Scrollable product-grid element.
 * @param {number} threshold Pixels remaining before loading another page.
 * @returns {boolean} Whether another page should be requested.
 */
export const shouldLoadMoreProducts = (order, grid, threshold = 160) => {
  if (!order || !grid || !order.productsHasMore || order.productsLoading) {
    return false
  }

  return grid.scrollTop + grid.clientHeight >= grid.scrollHeight - threshold
}

/**
 * Count products configured across all product categories.
 *
 * @param {Array} categories Product category summaries.
 * @returns {number} Total configured product count.
 */
export const countCategoryProducts = (categories = []) => {
  return categories.reduce((total, category) => {
    return total + Number(category.products_count || 0)
  }, 0)
}

/**
 * Resolve the selected customer for an order.
 *
 * @param {Array} customers Available customers.
 * @param {Object|null} order Current order.
 * @param {Object|null} fallback Default customer.
 * @returns {Object|null} Selected customer.
 */
export const findOrderCustomer = (customers = [], order, fallback = null) => {
  if (!order) return fallback

  return customers.find(customer => customer.customer_id === order.customer_id) || fallback
}

/**
 * Read a numeric total from an order.
 *
 * @param {Object|null} order Current order.
 * @param {string} field Order total field.
 * @returns {number} Numeric total.
 */
export const getOrderAmount = (order, field) => {
  return Number((order && order[field]) || 0)
}

/**
 * Count all quantities in an order cart.
 *
 * @param {Object|null} order Current order.
 * @returns {number} Total item quantity.
 */
export const getOrderItemCount = order => {
  return ((order && order.cartItems) || []).reduce((total, item) => {
    return total + Number(item.quantity || 0)
  }, 0)
}

/**
 * Get the next tab identifier for a list of retail orders.
 *
 * @param {Array} orders Current order tabs.
 * @returns {number} Next order identifier.
 */
export const getNextOrderId = (orders = []) => {
  return orders.length ? Math.max(...orders.map(order => order.id)) + 1 : 1
}
